<script setup lang="ts">
import type { SuccessResponseBody } from '#core/composables/useApiFetch'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@forgd/ui-kit/combobox'
import { ProjectAvatar } from '@forgd/ui-kit/blocks/avatars'
import { MagnifyingGlassIcon } from '@heroicons/vue/20/solid'

type CoinSearchResult = SuccessResponseBody<'/coins/search'>['coins'][number]

type CoinSelection = CoinSearchResult | 'not-in-list' | null

defineOptions({ inheritAttrs: false })

const emit = defineEmits<{
  change: [coin: CoinSearchResult]
  notInList: []
}>()

const model = defineModel<CoinSelection>({ default: null })

const NOT_IN_LIST = 'not-in-list'

const { open, query, debouncedQuery, filtered, loading, reset } = useAsyncComboboxFilter<CoinSearchResult>(
  async (q) => {
    const result = await apiFetch('/coins/search', {
      params: { query: { query: q } },
    })
    return result?.coins ?? []
  },
)

const selectedCoin = computed(() => (model.value && model.value !== NOT_IN_LIST ? model.value : null))
const isNotInList = computed(() => model.value === NOT_IN_LIST)

function handleSelect(opt: CoinSearchResult) {
  model.value = opt
  emit('change', opt)
  reset()
}

function handleSelectNotInList() {
  model.value = NOT_IN_LIST
  emit('notInList')
  reset()
}
</script>

<template>
  <Combobox v-model:open="open">
    <ComboboxTrigger
      v-bind="$attrs"
      data-ui="LConnectCoinSelect"
      size="md"
      placeholder="Search token by name or ticker..."
    >
      <div v-if="selectedCoin" class="flex items-center gap-2">
        <ProjectAvatar :src="selectedCoin.image" />
        <span class="text-base text-brand-900">{{ selectedCoin.name }}</span>
        <span class="text-sm text-gray-600">{{ selectedCoin.symbol }}</span>
      </div>
      <span v-else-if="isNotInList" class="text-base text-brand-900">My token does not appear in this list</span>
      <template #icon>
        <MagnifyingGlassIcon class="opacity-50" />
      </template>
    </ComboboxTrigger>
    <ComboboxContent>
      <ComboboxInput
        v-model="query"
        :loading="loading"
        type="search"
        role="searchbox"
        placeholder="Type at least 2 characters to start searching"
      />
      <ComboboxList>
        <ComboboxEmpty
          v-if="loading"
          class="items-start justify-start py-1.5 px-2 text-left text-placeholder"
        >
          Loading...
        </ComboboxEmpty>
        <ComboboxEmpty
          v-else-if="filtered.length === 0 && debouncedQuery.length < 2"
          class="items-start justify-start py-1.5 px-2 text-left text-placeholder"
        >
          No options
        </ComboboxEmpty>
        <ComboboxItem
          v-else-if="filtered.length === 0"
          :value="NOT_IN_LIST"
          data-testid="connect-coin-select-not-in-list"
          :selected="isNotInList"
          @select="handleSelectNotInList"
        >
          My token does not appear in this list
        </ComboboxItem>
        <template v-else>
          <ComboboxItem
            v-for="(opt, index) in filtered"
            :key="opt.id"
            :value="opt.id"
            :data-testid="`connect-coin-select-option-${index}`"
            :selected="selectedCoin?.id === opt.id"
            @select="handleSelect(opt)"
          >
            <div class="flex items-center gap-2">
              <ProjectAvatar :src="opt.image" size="md" />
              <span class="text-base text-brand-900">{{ opt.name }}</span>
              <span class="text-sm text-gray-600">{{ opt.symbol }}</span>
            </div>
          </ComboboxItem>
        </template>
      </ComboboxList>
    </ComboboxContent>
  </Combobox>
</template>
